import * as XLSX from "xlsx";
import { format } from "date-fns";
import {
  AnalystPerformance,
  ReviewerPerformance,
  Evaluation,
} from "@/graphql/types/models";

type SheetRow = Record<string, string | number | undefined>;

interface ExportSummary {
  readonly totalDocuments?: number;
  readonly totalFields?: number;
  readonly totalDatapoints?: number;
  readonly totalErrors?: number;
  readonly performancePercent?: number;
}

interface DateRange {
  readonly from?: Date;
  readonly to?: Date;
}

export interface ExportData {
  readonly analysts: readonly AnalystPerformance[];
  readonly reviewers: readonly ReviewerPerformance[];
  readonly analystEvaluations: readonly Evaluation[];
  readonly reviewerEvaluations: readonly Evaluation[];
  readonly analystSummary?: ExportSummary;
  readonly reviewerSummary?: ExportSummary;
  readonly dateRange?: DateRange;
}

function formatDate(date: Date | undefined): string {
  if (!date) return "N/A";
  return format(date, "yyyy-MM-dd");
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return format(new Date(dateStr), "yyyy-MM-dd HH:mm:ss");
}

function calculateColumnWidths(rows: readonly SheetRow[]): XLSX.ColInfo[] {
  if (rows.length === 0) return [];

  const firstRow = rows[0];
  if (!firstRow) return [];

  const keys = Object.keys(firstRow);
  return keys.map((key) => {
    const maxContentLength = rows.reduce((max, row) => {
      const value = row[key];
      const len = value != null ? String(value).length : 0;
      return Math.max(max, len);
    }, key.length);

    return { wch: Math.min(Math.max(maxContentLength + 2, 12), 50) };
  });
}

function addSheetToWorkbook(
  workbook: XLSX.WorkBook,
  rows: readonly SheetRow[],
  sheetName: string,
): void {
  if (rows.length === 0) return;

  const sheet = XLSX.utils.json_to_sheet([...rows]);
  sheet["!cols"] = calculateColumnWidths(rows);
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
}

function createSummarySheet(
  analystSummary: ExportSummary | undefined,
  reviewerSummary: ExportSummary | undefined,
  dateRange: DateRange | undefined,
): XLSX.WorkSheet {
  const dateRangeStr = `${formatDate(dateRange?.from)} to ${formatDate(dateRange?.to)}`;

  const data: (string | number)[][] = [
    ["Evaluation Dashboard Export"],
    [`Generated: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`],
    [`Date Range: ${dateRangeStr}`],
    [],
  ];

  if (analystSummary) {
    data.push(
      ["Analyst Summary"],
      [],
      ["Metric", "Value"],
      ["Total Documents", analystSummary.totalDocuments ?? 0],
      ["Total Fields", analystSummary.totalFields ?? 0],
      ["Total Datapoints", analystSummary.totalDatapoints ?? 0],
      ["Total Errors", analystSummary.totalErrors ?? 0],
      [
        "Overall Score",
        `${(analystSummary.performancePercent ?? 0).toFixed(1)}%`,
      ],
      [],
    );
  }

  if (reviewerSummary) {
    data.push(
      [],
      ["Reviewer Summary"],
      [],
      ["Metric", "Value"],
      ["Total Documents", reviewerSummary.totalDocuments ?? 0],
      ["Total Fields", reviewerSummary.totalFields ?? 0],
      ["Total Datapoints", reviewerSummary.totalDatapoints ?? 0],
      ["Total Errors", reviewerSummary.totalErrors ?? 0],
      [
        "Overall Score",
        `${(reviewerSummary.performancePercent ?? 0).toFixed(1)}%`,
      ],
    );
  }

  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet["!cols"] = [{ wch: 25 }, { wch: 35 }];
  return sheet;
}

function createAnalystPerformanceRows(
  data: readonly AnalystPerformance[],
): SheetRow[] {
  return data.map(
    (node): SheetRow => ({
      Name: node.personName ?? "",
      Email: node.personEmail ?? "",
      Documents: node.totalDocuments ?? 0,
      Fields: node.totalFields ?? 0,
      Datapoints: node.totalDatapoints ?? 0,
      Correct: node.totalCorrect ?? 0,
      Errors: node.totalErrors ?? 0,
      "Score (%)":
        node.performancePercent != null
          ? Number(node.performancePercent.toFixed(2))
          : 0,
      "Daily Velocity":
        node.dailyVelocity != null ? Number(node.dailyVelocity.toFixed(2)) : 0,
      "Work Days": node.workDays ?? 0,
    }),
  );
}

function createReviewerPerformanceRows(
  data: readonly ReviewerPerformance[],
): SheetRow[] {
  return data.map(
    (node): SheetRow => ({
      Name: node.personName ?? "",
      Email: node.personEmail ?? "",
      Documents: node.totalDocuments ?? 0,
      Fields: node.totalFields ?? 0,
      Datapoints: node.totalDatapoints ?? 0,
      Correct: node.totalCorrect ?? 0,
      Errors: node.totalErrors ?? 0,
      "Score (%)":
        node.performancePercent != null
          ? Number(node.performancePercent.toFixed(2))
          : 0,
      "Daily Velocity":
        node.dailyVelocity != null ? Number(node.dailyVelocity.toFixed(2)) : 0,
      "Review Days": node.reviewDays ?? 0,
      "Avg Review Time (min)":
        node.avgReviewTimeMinutes != null
          ? Number(node.avgReviewTimeMinutes.toFixed(1))
          : 0,
    }),
  );
}

function createErrorRows(
  data: readonly (AnalystPerformance | ReviewerPerformance)[],
): SheetRow[] {
  const rows: SheetRow[] = [];

  for (const node of data) {
    const breakdown = node.errorBreakdown;
    if (!breakdown || breakdown.length === 0) continue;

    for (const error of breakdown) {
      rows.push({
        Name: node.personName ?? "",
        "Error Type": error.errorType ?? "",
        Count: error.total ?? 0,
        "Percentage (%)":
          error.percentage != null ? Number(error.percentage.toFixed(2)) : 0,
        "Documents Affected": error.documentsAffected ?? 0,
        "Fields Affected": error.fieldsAffected ?? 0,
        Weight: error.weight != null ? Number(error.weight.toFixed(2)) : 0,
      });
    }
  }

  return rows;
}

function createEvaluationRows(evaluations: readonly Evaluation[]): SheetRow[] {
  return evaluations.map(
    (e): SheetRow => ({
      "Person Name": e.person?.name ?? "",
      "Person Email": e.person?.email ?? "",
      "Document Name": e.document?.name ?? "",
      "Company Name": e.document?.companyName ?? "",
      "Fund Name": e.document?.fundName ?? "",
      "Document Type": e.document?.type ?? "",
      "Field Name": e.field?.displayName || e.field?.name || "",
      "Field Category": e.field?.category ?? "",
      "Datapoint Name": e.datapoint?.displayName || e.datapoint?.name || "",
      "Datapoint Type": e.datapoint?.dataType ?? "",
      "Submitted Value": e.value ?? "",
      "Previous Value": e.previousValue ?? "",
      "Correct Value": e.finalValue ?? "",
      "Is Final": e.isFinal ? "Yes" : "No",
      "Error Type": e.classification ?? "",
      "Classification Reason": e.classificationReason ?? "",
      Confidence:
        e.classificationConfidence != null
          ? Number((e.classificationConfidence * 100).toFixed(1))
          : "",
      "AI Model": e.aiModel ?? "",
      "Submitted At": formatDateTime(e.submittedAt),
      "Classified At": formatDateTime(e.classifiedAt),
    }),
  );
}

function addAnalystSheets(
  workbook: XLSX.WorkBook,
  performanceData: readonly AnalystPerformance[],
  evaluations: readonly Evaluation[],
): void {
  if (performanceData.length > 0) {
    const perfRows = createAnalystPerformanceRows(performanceData);
    addSheetToWorkbook(workbook, perfRows, "Analysts");

    const errorRows = createErrorRows(performanceData);
    if (errorRows.length > 0) {
      addSheetToWorkbook(workbook, errorRows, "Analysts Errors");
    }
  }

  if (evaluations.length > 0) {
    const evalRows = createEvaluationRows(evaluations);
    addSheetToWorkbook(workbook, evalRows, "Analysts Details");
  }
}

function addReviewerSheets(
  workbook: XLSX.WorkBook,
  performanceData: readonly ReviewerPerformance[],
  evaluations: readonly Evaluation[],
): void {
  if (performanceData.length > 0) {
    const perfRows = createReviewerPerformanceRows(performanceData);
    addSheetToWorkbook(workbook, perfRows, "Reviewers");

    const errorRows = createErrorRows(performanceData);
    if (errorRows.length > 0) {
      addSheetToWorkbook(workbook, errorRows, "Reviewers Errors");
    }
  }

  if (evaluations.length > 0) {
    const evalRows = createEvaluationRows(evaluations);
    addSheetToWorkbook(workbook, evalRows, "Reviewers Details");
  }
}

export function exportPerformanceToExcel(data: ExportData): void {
  const {
    analysts,
    reviewers,
    analystEvaluations,
    reviewerEvaluations,
    analystSummary,
    reviewerSummary,
    dateRange,
  } = data;

  const workbook = XLSX.utils.book_new();

  const summarySheet = createSummarySheet(
    analystSummary,
    reviewerSummary,
    dateRange,
  );
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  addAnalystSheets(workbook, analysts, analystEvaluations);
  addReviewerSheets(workbook, reviewers, reviewerEvaluations);

  const dateStr = format(new Date(), "yyyy-MM-dd");
  const fileName = `evaluation_dashboard_${dateStr}.xlsx`;

  XLSX.writeFile(workbook, fileName);
}
